<template>
  <div class="min-h-screen bg-gradient-to-br from-[#F8FAFF] to-[#F0F7FF] dark:from-[#0a0a0a] dark:to-[#1a1a1a] p-6">
    <div class="max-w-7xl mx-auto">
      
      <!-- Header with Add Product Button -->
      <div class="flex items-center justify-between mb-8">
        <div>
          <h1 class="text-3xl font-bold bg-gradient-to-r from-[#1a1a1a] to-[#666666] dark:from-white dark:to-[#cccccc] bg-clip-text text-transparent">
            {{ directT('ecommerce.productsGrid.title') || 'רשת מוצרים' }}
          </h1>
          <p class="text-[#666666] dark:text-[#cccccc] mt-2">
            {{ directT('ecommerce.productsGrid.subtitle') || 'נהל וצפה בקטלוג המוצרים שלך' }}
          </p>
        </div>
        
        <button
          @click="navigateToCreateProduct"
          class="group flex items-center gap-3 px-6 py-3 bg-gradient-to-r from-[#007AFF] to-[#5856D6] text-white rounded-2xl font-semibold shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 hover:from-[#0056CC] hover:to-[#4A47B8]"
        >
          <div class="w-5 h-5 rounded-lg bg-white/20 flex items-center justify-center">
            <i class="material-symbols-outlined text-sm">add</i>
          </div>
          <span>{{ directT('ecommerce.productsGrid.addNewProduct') || 'הוסף מוצר חדש' }}</span>
          <i class="material-symbols-outlined text-sm group-hover:translate-x-1 transition-transform duration-300">arrow_forward</i>
        </button>
      </div>
      
      <!-- Loading State -->
      <div v-if="loading" class="flex flex-col items-center justify-center py-20">
        <div class="relative">
          <div class="w-16 h-16 border-4 border-[#007AFF]/30 border-t-[#007AFF] rounded-full animate-spin"></div>
          <div class="absolute inset-0 w-16 h-16 border-4 border-transparent border-r-[#34C759] rounded-full animate-spin" style="animation-delay: -0.3s;"></div>
        </div>
        <p class="mt-6 text-lg font-medium text-[#666666] dark:text-[#cccccc]">{{ directT('ecommerce.productsGrid.loadingProducts') || 'טוען מוצרים...' }}</p>
      </div>
      
      <!-- Empty State - No products at all -->
      <div v-else-if="allProducts && allProducts.length === 0" class="flex flex-col items-center justify-center py-20">
        <div class="w-24 h-24 bg-gradient-to-br from-[#007AFF] to-[#5856D6] rounded-3xl flex items-center justify-center mb-6 shadow-xl">
          <i class="material-symbols-outlined text-white text-5xl">inventory_2</i>
        </div>
        <h3 class="text-2xl font-bold text-[#1a1a1a] dark:text-white mb-2">{{ directT('ecommerce.productsGrid.noProductsTitle') || 'אין מוצרים' }}</h3>
        <p class="text-[#666666] dark:text-[#cccccc] text-center max-w-md">{{ directT('ecommerce.productsGrid.noProductsFound') || 'לא נמצאו מוצרים במאגר' }}</p>
      </div>
      
      <!-- No Filter Results State -->
      <div v-else-if="allProducts && allProducts.length > 0 && products && products.length === 0" class="flex flex-col items-center justify-center py-20">
        <div class="w-24 h-24 bg-gradient-to-br from-[#FF9500] to-[#FFB020] rounded-3xl flex items-center justify-center mb-6 shadow-xl">
          <i class="material-symbols-outlined text-white text-5xl">search_off</i>
        </div>
        <h3 class="text-2xl font-bold text-[#1a1a1a] dark:text-white mb-2">{{ directT('ecommerce.productsGrid.noResultsTitle') || 'אין תוצאות' }}</h3>
        <p class="text-[#666666] dark:text-[#cccccc] text-center max-w-md">{{ directT('ecommerce.productsGrid.noResultsFound') || 'לא נמצאו מוצרים התואמים לחיפוש' }}</p>
      </div>
      
      <!-- Products Grid -->
      <div v-else-if="products && products.length > 0" class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
        <div
          v-for="product in products"
          :key="product.id"
          @click="navigateToEdit(product.id)"
          class="group bg-white dark:bg-[#1a1a1a] rounded-3xl shadow-lg hover:shadow-2xl border border-[#E5E7EB] dark:border-[#374151] overflow-hidden cursor-pointer transition-all duration-500 hover:-translate-y-2 hover:scale-[1.02] flex flex-col"
        >
          <!-- Product Image Container -->
          <div class="relative overflow-hidden rounded-t-3xl aspect-square bg-gradient-to-br from-[#F8FAFF] to-[#E6F3FF] dark:from-[#0c1427] dark:to-[#1a2642]">
            <img 
              :src="product.image_link" 
              :alt="product.title" 
              class="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" 
              onerror="this.src='/images/placeholder-product.png'"
            />
            
            <!-- Gradient Overlay -->
            <div class="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            
            <!-- Sale Badge -->
            <div v-if="product.compare_at_price && product.compare_at_price > product.price" 
                 class="absolute top-4 right-4 bg-gradient-to-r from-[#FF3B30] to-[#FF2D92] text-white px-3 py-1 rounded-full text-sm font-bold shadow-lg">
              -{{ Math.round(((product.compare_at_price - product.price) / product.compare_at_price) * 100) }}%
            </div>
            
            <!-- Action Button -->
            <div class="absolute bottom-4 right-4 opacity-0 group-hover:opacity-100 transform translate-y-4 group-hover:translate-y-0 transition-all duration-300">
              <a
                :href="product.product_link"
                target="_blank"
                @click.stop
                class="w-12 h-12 bg-gradient-to-br from-[#007AFF] to-[#5856D6] text-white rounded-2xl flex items-center justify-center shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-110"
              >
                <i class="material-symbols-outlined text-xl">shopping_cart</i>
              </a>
            </div>
            
            <!-- Quick View Button -->
            <div class="absolute bottom-4 left-4 opacity-0 group-hover:opacity-100 transform translate-y-4 group-hover:translate-y-0 transition-all duration-300" style="transition-delay: 0.1s;">
              <button class="w-12 h-12 bg-white/90 dark:bg-black/90 text-[#1a1a1a] dark:text-white rounded-2xl flex items-center justify-center shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-110">
                <i class="material-symbols-outlined text-xl">visibility</i>
              </button>
            </div>
          </div>
          
          <!-- Product Info -->
          <div class="p-6 flex-1 flex flex-col">
            <!-- Product Title -->
            <h3 class="text-lg font-bold text-[#1a1a1a] dark:text-white mb-3 line-clamp-2 group-hover:text-[#007AFF] transition-colors duration-300">
              {{ product.title }}
            </h3>
            
            <!-- Price Section -->
            <div class="flex items-center justify-between">
              <div class="flex items-center gap-3">
                <span class="text-2xl font-bold bg-gradient-to-r from-[#007AFF] to-[#5856D6] bg-clip-text text-transparent">
                  {{ formatPrice(product.price) }}
                </span>
                <span v-if="product.compare_at_price && product.compare_at_price > product.price" 
                      class="text-sm text-[#666666] dark:text-[#cccccc] line-through">
                  {{ formatPrice(product.compare_at_price) }}
                </span>
              </div>
              
              <!-- Rating Stars (placeholder) -->
              <div class="flex items-center gap-1">
                <div class="flex" v-for="i in 5" :key="i">
                  <i class="material-symbols-outlined text-[#FFD700] text-sm">star</i>
                </div>
                <span class="text-xs text-[#666666] dark:text-[#cccccc] ml-1">(4.8)</span>
              </div>
            </div>
            
            <!-- Spacer to push tags to bottom -->
            <div class="flex-1"></div>
            
            <!-- Product Tags -->
            <div class="flex flex-wrap gap-2 mt-4">
              <span class="px-3 py-1 bg-gradient-to-r from-[#34C759]/10 to-[#22C55E]/10 text-[#34C759] dark:text-[#22C55E] text-xs font-medium rounded-full border border-[#34C759]/20">
                {{ directT('ecommerce.productsGrid.inStock') }}
              </span>
              <span class="px-3 py-1 bg-gradient-to-r from-[#FF9500]/10 to-[#FFB020]/10 text-[#FF9500] dark:text-[#FFB020] text-xs font-medium rounded-full border border-[#FF9500]/20">
                {{ directT('ecommerce.productsGrid.fastShipping') }}
              </span>
            </div>
          </div>
          
          <!-- Card Footer -->
          <div class="border-t border-[#E5E7EB] dark:border-[#374151] px-6 py-4 bg-gradient-to-r from-[#F8FAFF] to-[#F0F7FF] dark:from-[#0c1427] dark:to-[#1a2642]">
                          <div class="flex items-center justify-between">
                <div class="flex items-center gap-2">
                  <div class="w-2 h-2 bg-[#34C759] rounded-full animate-pulse"></div>
                  <span class="text-xs text-[#666666] dark:text-[#cccccc]">{{ directT('ecommerce.productsGrid.active') }}</span>
                </div>
                <button class="text-[#007AFF] hover:text-[#0056CC] text-sm font-medium transition-colors duration-200">
                  {{ directT('ecommerce.productsGrid.editProduct') }} ←
                </button>
              </div>
          </div>
        </div>
      </div>
      
      <!-- Enhanced Pagination -->
      <div v-if="products && products.length > 0 && totalProducts > pageSize" class="mt-12">
        <div class="bg-white dark:bg-[#1a1a1a] rounded-3xl shadow-xl border border-[#E5E7EB] dark:border-[#374151] p-8">
          <div class="flex flex-col sm:flex-row items-center justify-between gap-6">
            <div class="flex items-center gap-4">
              <div class="w-12 h-12 bg-gradient-to-br from-[#007AFF] to-[#5856D6] rounded-2xl flex items-center justify-center">
                <i class="material-symbols-outlined text-white text-xl">apps</i>
              </div>
              <div>
                <p class="text-lg font-bold text-[#1a1a1a] dark:text-white">{{ directT('ecommerce.productsGrid.totalProducts', { count: totalProducts }) }}</p>
                <p class="text-sm text-[#666666] dark:text-[#cccccc]">{{ directT('ecommerce.productsGrid.showingProducts', { from: Math.min(pageSize * currentPage, totalProducts), total: totalProducts }) }}</p>
              </div>
            </div>
            
            <Pagination 
              :items="pageSize" 
              :total="totalProducts" 
              :current="currentPage"
              @page-change="handlePageChange"
              class="pagination-enhanced"
            />
          </div>
        </div>
      </div>
      
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted, watch, computed } from "vue";
import Pagination from "@/components/Common/Pagination.vue";
import { formatCurrency, getUserCurrency, getUserCurrencySymbol } from "@/utils/currency";
import { supabase } from "@/lib/supabaseClient";
import { useRouter } from "vue-router";
import { useI18n } from "vue-i18n";
import enTranslations from "@/i18n/locales/en.json";
import heTranslations from "@/i18n/locales/he.json";
import arTranslations from "@/i18n/locales/ar.json";

interface Product {
  id: string;
  title: string;
  image_link: string;
  price: number;
  compare_at_price: number | null;
  product_link: string;
  org_id: string;
  category?: string;
  created_at?: string;
}

// Type definition for translation objects
type TranslationObject = {
  [key: string]: string | TranslationObject;
};

export default defineComponent({
  name: "ProductsGrid",
  components: {
    Pagination,
  },
  props: {
    filters: {
      type: Object as () => {
        search: string;
        category: string;
        sort: string;
        minPrice: number | null;
        maxPrice: number | null;
      },
      default: () => ({
        search: '',
        category: '',
        sort: '',
        minPrice: null,
        maxPrice: null
      })
    }
  },
  setup(props) {
    console.log('ProductsGrid setup starting...');
    const router = useRouter();
    const allProducts = ref<Product[]>([]);
    const loading = ref(true);
    const error = ref<string | null>(null);
    const currentPage = ref(1);
    const pageSize = ref(9);
    const currencyCode = ref('ILS'); // ברירת מחדל
    const organizationId = ref<string | null>(null);

    console.log('ProductsGrid allProducts initialized:', allProducts.value);

    // Use filters from props
    const searchQuery = computed(() => props.filters.search);
    const selectedCategory = computed(() => props.filters.category);
    const selectedSort = computed(() => props.filters.sort);
    const minPrice = computed(() => props.filters.minPrice);
    const maxPrice = computed(() => props.filters.maxPrice);

    // Filter products function
    const filterProducts = (products: Product[]): Product[] => {
      console.log('Filtering products. Input:', products.length);
      console.log('Current filters:', {
        search: searchQuery.value,
        category: selectedCategory.value,
        sort: selectedSort.value,
        minPrice: minPrice.value,
        maxPrice: maxPrice.value
      });
      
      let filteredProducts = [...products];

      // Search filter
      if (searchQuery.value) {
        const searchTerm = searchQuery.value.toLowerCase();
        filteredProducts = filteredProducts.filter(product =>
          product.title.toLowerCase().includes(searchTerm)
        );
        console.log('After search filter:', filteredProducts.length);
      }

      // Category filter
      if (selectedCategory.value && selectedCategory.value !== 'ecommerce.productsGrid.categories.allCategories') {
        filteredProducts = filteredProducts.filter(product =>
          product.category === selectedCategory.value
        );
      }

      // Price range filter
      if (minPrice.value !== null) {
        filteredProducts = filteredProducts.filter(product =>
          product.price >= minPrice.value!
        );
        console.log('After min price filter:', filteredProducts.length);
      }

      if (maxPrice.value !== null) {
        filteredProducts = filteredProducts.filter(product =>
          product.price <= maxPrice.value!
        );
        console.log('After max price filter:', filteredProducts.length);
      }

      // Sort products
      if (selectedSort.value) {
        switch (selectedSort.value) {
          case 'ecommerce.productsGrid.sortOptions.priceLowHigh':
            filteredProducts.sort((a, b) => a.price - b.price);
            break;
          case 'ecommerce.productsGrid.sortOptions.priceHighLow':
            filteredProducts.sort((a, b) => b.price - a.price);
            break;
          case 'ecommerce.productsGrid.sortOptions.nameAsc':
            filteredProducts.sort((a, b) => a.title.localeCompare(b.title));
            break;
          case 'ecommerce.productsGrid.sortOptions.nameDesc':
            filteredProducts.sort((a, b) => b.title.localeCompare(a.title));
            break;
          case 'ecommerce.productsGrid.sortOptions.newest':
            filteredProducts.sort((a, b) => {
              const dateA = new Date(a.created_at || '').getTime();
              const dateB = new Date(b.created_at || '').getTime();
              return dateB - dateA;
            });
            break;
          default:
            // Default sort (no change)
            break;
        }
      }

      console.log('Final filtered products:', filteredProducts.length);
      return filteredProducts;
    };

    // Computed filtered products
    const filteredProducts = computed(() => {
      try {
        if (!allProducts.value || allProducts.value.length === 0) {
          return [];
        }
        return filterProducts(allProducts.value);
      } catch (error) {
        console.error('Error filtering products:', error);
        return allProducts.value || [];
      }
    });

    // Computed paginated products
    const products = computed(() => {
      try {
        if (!filteredProducts.value || filteredProducts.value.length === 0) {
          return [];
        }
        const startIndex = (currentPage.value - 1) * pageSize.value;
        const endIndex = startIndex + pageSize.value;
        return filteredProducts.value.slice(startIndex, endIndex);
      } catch (error) {
        console.error('Error paginating products:', error);
        return [];
      }
    });

    // Computed total products (based on filtered results)
    const totalProducts = computed(() => {
      try {
        return filteredProducts.value ? filteredProducts.value.length : 0;
      } catch (error) {
        console.error('Error computing total products:', error);
        return 0;
      }
    });

    // i18n setup
    const { locale } = useI18n();

    // Translation files
    const translationFiles = {
      en: enTranslations,
      he: heTranslations,
      ar: arTranslations
    };

    // Direct translation function that bypasses vue-i18n
    const directT = (key: string, params?: Record<string, any>): string => {
      const parts = key.split('.');
      let result: TranslationObject = translationFiles[locale.value as keyof typeof translationFiles];
      
      for (const part of parts) {
        if (!result || !(part in result)) {
          console.warn(`Missing translation key: ${key} in ${locale.value} locale`);
          // Fallback to English if translation not found
          let enResult: TranslationObject = translationFiles.en;
          for (const fallbackPart of parts) {
            if (!enResult || !(fallbackPart in enResult)) return key;
            enResult = enResult[fallbackPart as keyof typeof enResult] as TranslationObject;
          }
          result = enResult;
          break;
        }
        result = result[part as keyof typeof result] as TranslationObject;
      }
      
      let translation = typeof result === 'string' ? result : key;
      
      // Replace parameters if provided
      if (params) {
        Object.keys(params).forEach(paramKey => {
          translation = translation.replace(new RegExp(`\\{${paramKey}\\}`, 'g'), String(params[paramKey]));
        });
      }
      
      return translation;
    };

    // Get the current user's organization ID
    const getUserOrganizationId = async (): Promise<string | null> => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return null;

        const { data: profileData } = await supabase
          .from('user_profiles')
          .select('org_id')
          .eq('user_id', user.id)
          .single();

        return profileData?.org_id as string || null;
      } catch (err) {
        console.error('Error fetching user organization:', err);
        return null;
      }
    };

    // Fetch products from the database
    const fetchProducts = async () => {
      loading.value = true;
      error.value = null;
      
      try {
        if (!organizationId.value) {
          organizationId.value = await getUserOrganizationId();
        }
        
        if (!organizationId.value) {
          throw new Error("Could not determine organization ID");
        }
        
        console.log("Fetching products for organization:", organizationId.value);
        
        // Fetch all products (we'll handle pagination on client side after filtering)
        console.log("Fetching all products for organization:", organizationId.value);
        
        const { data, error: fetchError } = await supabase
          .from('products')
          .select('*')
          .eq('org_id', organizationId.value)
          .order('created_at', { ascending: false });
          
        if (fetchError) {
          console.error("Error fetching products:", fetchError);
          throw fetchError;
        }
        
        console.log("Products data received:", data);
        
        // Type safety conversion
        const typedData = data || [];
        
        if (typedData.length === 0) {
          console.log("No products found for this organization, checking for any products...");
          
          // Check if there are any products at all in the database
          const { data: anyProducts, error: anyError } = await supabase
            .from('products')
            .select('id, org_id')
            .limit(5);
            
          if (anyError) {
            console.error("Error checking for any products:", anyError);
          } else {
            console.log("Sample products in database:", anyProducts);
          }
        }
        
        allProducts.value = typedData.map(item => ({
          id: String(item.id || ''),
          title: String(item.title || ''),
          image_link: String(item.image_link || ''),
          price: Number(item.price || 0),
          compare_at_price: item.compare_at_price ? Number(item.compare_at_price) : null,
          product_link: String(item.product_link || ''),
          org_id: String(item.org_id || ''),
          category: String(item.category || ''),
          created_at: String(item.created_at || '')
        }));
        
        console.log("Processed products:", allProducts.value);
      } catch (err) {
        console.error('Error fetching products:', err);
        error.value = 'Failed to load products. Please try again.';
      } finally {
        loading.value = false;
      }
    };

    // Handle pagination
    const handlePageChange = (page: number) => {
      currentPage.value = page;
    };
    
    // Watch for filter changes to reset to first page
    watch(
      () => props.filters,
      (newFilters) => {
        console.log('Filters changed in ProductsGrid:', newFilters);
        currentPage.value = 1;
      },
      { deep: true }
    );

    // Initialize
    onMounted(async () => {
      try {
        // Load currency
        currencyCode.value = await getUserCurrency();
        
        // Fetch products
        await fetchProducts();
      } catch (error) {
        console.error('Error in component initialization:', error);
      }
    });

    const formatPrice = (price: number) => {
      return formatCurrency(price, currencyCode.value);
    };

    const navigateToEdit = (productId: string) => {
      router.push(`/ecommerce/edit-product?id=${productId}&bypass_auth=true`);
    };

    const navigateToCreateProduct = () => {
      router.push('/ecommerce/create-product');
    };

    return {
      products,
      allProducts,
      filteredProducts,
      loading,
      error,
      currentPage,
      pageSize,
      totalProducts,
      formatPrice,
      handlePageChange,
      navigateToEdit,
      navigateToCreateProduct,
      directT
    };
  },
});
</script>

<style lang="scss" scoped>
/* Modern product grid enhancements */
.line-clamp-2 {
  overflow: hidden;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
}

/* Enhanced pagination styling */
.pagination-enhanced {
  :deep(.pagination-wrapper) {
    .pagination-btn {
      @apply bg-gradient-to-br from-[#F8FAFF] to-[#E6F3FF] dark:from-[#1a2642] dark:to-[#0c1427];
      @apply border-2 border-[#E5E7EB] dark:border-[#374151];
      @apply text-[#666666] dark:text-[#cccccc];
      @apply rounded-2xl transition-all duration-300;
      @apply hover:bg-gradient-to-br hover:from-[#007AFF] hover:to-[#5856D6];
      @apply hover:text-white hover:border-[#007AFF];
      @apply hover:shadow-lg hover:scale-105;
    }
    
    .pagination-btn.active {
      @apply bg-gradient-to-br from-[#007AFF] to-[#5856D6];
      @apply text-white border-[#007AFF];
      @apply shadow-xl;
    }
  }
}

/* Product card animations */
.group {
  transform-style: preserve-3d;
  
  &:hover {
    .product-image {
      transform: scale(1.05) rotateY(2deg);
    }
  }
}

/* Custom hover effects */
.hover-lift {
  transition: all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94);
}

.hover-lift:hover {
  transform: translateY(-8px) scale(1.02);
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
}

/* Loading animation enhancement */
@keyframes shimmer {
  0% {
    background-position: -468px 0;
  }
  100% {
    background-position: 468px 0;
  }
}

.shimmer {
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 400% 100%;
  animation: shimmer 1.5s infinite;
}

/* Star rating styling */
.star-rating {
  .star {
    transition: all 0.2s ease-in-out;
    
    &:hover {
      transform: scale(1.2);
    }
  }
}

/* Badge animations */
.sale-badge {
  animation: pulse-glow 2s infinite;
}

@keyframes pulse-glow {
  0%, 100% {
    box-shadow: 0 0 0 0 rgba(255, 59, 48, 0.7);
  }
  50% {
    box-shadow: 0 0 0 10px rgba(255, 59, 48, 0);
  }
}

/* Mobile responsive enhancements */
@media (max-width: 768px) {
  .product-card {
    margin-bottom: 1.5rem;
  }
  
  .pagination-enhanced {
    :deep(.pagination-wrapper) {
      .pagination-btn {
        @apply px-3 py-2 text-sm;
      }
    }
  }
}

/* Dark mode specific enhancements */
@media (prefers-color-scheme: dark) {
  .dark-glow:hover {
    box-shadow: 0 25px 50px -12px rgba(0, 122, 255, 0.15);
  }
}

/* Product tag styles */
.product-tag {
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
}

/* Enhanced button hover effects */
.btn-enhanced {
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
    transition: left 0.5s;
  }
  
  &:hover::before {
    left: 100%;
  }
}

/* Grid layout optimizations */
.products-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 2rem;
  
  @media (min-width: 1200px) {
    grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  }
}

/* Status indicator */
.status-indicator {
  &.active {
    @apply bg-[#34C759];
    box-shadow: 0 0 0 4px rgba(52, 199, 89, 0.2);
    animation: pulse 2s infinite;
  }
}

@keyframes pulse {
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
}
</style>
